import api from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('availableStrategiesService')

/**
 * Service for fetching available challenge strategies
 */
class AvailableStrategiesService {
  constructor() {
    this.cache = null
    this.cacheTimestamp = null
    this.CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  }

  /**
   * Fetch all available strategies from the API
   * @returns {Promise<Array>} Array of available strategy objects
   */
  async getAvailableStrategies() {
    try {
      // Check cache first
      if (this.cache && this.cacheTimestamp) {
        const now = Date.now()
        if (now - this.cacheTimestamp < this.CACHE_DURATION) {
          return this.cache
        }
      }

      // Fetch from API
      const response = await api.get('/challenges/strategies/available')
      const strategies = response.data?.data || []

      // Update cache
      this.cache = strategies
      this.cacheTimestamp = Date.now()

      return strategies
    } catch (error) {
      logger.error('Error fetching available strategies:', error)
      // Return cache if available, otherwise empty array
      return this.cache || []
    }
  }

  /**
   * Get a single strategy by type
   * @param {string} strategyType - The strategy type (e.g., 'SAT_MATH')
   * @returns {Promise<Object|null>} Strategy object or null if not found
   */
  async getStrategy(strategyType) {
    try {
      const strategies = await this.getAvailableStrategies()
      return strategies.find((s) => (s.strategyType || s.type) === strategyType) || null
    } catch (error) {
      logger.error(`Error fetching strategy ${strategyType}:`, error)
      return null
    }
  }

  /**
   * Invalidate the cache
   */
  invalidateCache() {
    this.cache = null
    this.cacheTimestamp = null
  }

  /**
   * Get strategy display name
   * @param {string} strategyType - The strategy type
   * @returns {string} Display name
   */
  getStrategyDisplayName(strategyType) {
    const names = {
      SAT_MATH: 'SAT Math',
      SAT_READING_WRITING: 'SAT Reading & Writing',
      SAT_MATH_MINI: 'SAT Math Mini'
    }
    return names[strategyType] || strategyType
  }

  /**
   * Get strategy icon
   * @param {string} strategyType - The strategy type
   * @returns {string} Icon emoji
   */
  getStrategyIcon(strategyType) {
    const icons = {
      SAT_MATH: '🔢',
      SAT_READING_WRITING: '📚',
      SAT_MATH_MINI: '🎯'
    }
    return icons[strategyType] || '📝'
  }

  /**
   * Get strategy description
   * @param {Object} strategy - Strategy object from API
   * @returns {string} Description
   */
  getStrategyDescription(strategy) {
    if (!strategy) return ''

    const { description, questionCount, totalQuestions } = strategy

    if (description) {
      return description
    }

    // Fallback descriptions based on type
    const fallbacks = {
      SAT_MATH: `Full SAT Math assessment with ${totalQuestions || 'multiple'} questions covering all domains`,
      SAT_READING_WRITING: `Comprehensive SAT Reading & Writing test with ${totalQuestions || 'multiple'} questions`,
      SAT_MATH_MINI: `Quick SAT Math assessment with ${questionCount || totalQuestions || 'focused'} questions`
    }

    return fallbacks[strategy.type] || 'Challenge your skills with this strategy'
  }
}

// Create and export singleton instance
const availableStrategiesService = new AvailableStrategiesService()
export default availableStrategiesService
